import { useState } from 'react';
import { createCourse, updateCourse } from '../../services/content/courseService';
import { ImageUploadField } from './ImageUploadField';
import type { Course } from '../../types/content/course';

const statusOptions = ['draft', 'published'];

interface CourseModalProps {
  course?: Course;
  onClose: () => void;
  onSuccess: () => void;
}

export function CourseModal({ course, onClose, onSuccess }: CourseModalProps) {
  const isEdit = !!course;

  const [name, setName] = useState(course?.name || '');
  const [slug, setSlug] = useState(course?.slug || '');
  const [status, setStatus] = useState(course?.status || 'draft');
  const [description, setDescription] = useState(course?.description || '');
  const [quizTitle, setQuizTitle] = useState(course?.quizTitle || '');
  const [gameTitle, setGameTitle] = useState(course?.gameTitle || '');
  const [imageUrl, setImageUrl] = useState(course?.imageUrl || '');
  const [thumbNailUrl, setThumbNailUrl] = useState(course?.thumbNailUrl || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  function generateSlug(value: string) {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  async function handleSave() {
    if (!name.trim() || !slug.trim()) return;
    setError('');
    setLoading(true);
    try {
      const data = {
        name: name.trim(),
        slug: slug.trim(),
        status,
        description: description.trim(),
        quizTitle: quizTitle.trim() || null,
        gameTitle: gameTitle.trim() || null,
      };
      if (isEdit) {
        await updateCourse(course!.id, { ...course!, ...data });
      } else {
        await createCourse(data);
      }
      onSuccess();
    } catch {
      setError(isEdit ? 'Failed to update course' : 'Failed to create course');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {isEdit ? 'Edit Course' : 'Add Course'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className={labelClass}>Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!isEdit) setSlug(generateSlug(e.target.value));
                }}
                className={inputClass}
                placeholder="Course name"
              />
            </div>
            <div>
              <label className={labelClass}>Slug *</label>
              <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} className={inputClass} placeholder="course-slug" />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
                {statusOptions.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} rows={3} placeholder="Course description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Quiz Title</label>
                <input type="text" value={quizTitle} onChange={(e) => setQuizTitle(e.target.value)} className={inputClass} placeholder="Quiz title" />
              </div>
              <div>
                <label className={labelClass}>Game Title</label>
                <input type="text" value={gameTitle} onChange={(e) => setGameTitle(e.target.value)} className={inputClass} placeholder="Game title" />
              </div>
            </div>
            {isEdit && course?.id && (
              <div className="grid grid-cols-2 gap-4">
                <ImageUploadField
                  label="Course Image"
                  currentUrl={imageUrl}
                  uploadEndpoint={`/course/${course.id}/image`}
                  onUploaded={() => onSuccess()}
                />
                <ImageUploadField
                  label="Thumbnail"
                  currentUrl={thumbNailUrl}
                  uploadEndpoint={`/course/${course.id}/thumbnail`}
                  onUploaded={() => onSuccess()}
                />
              </div>
            )}
          </div>

          {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={loading || !name.trim() || !slug.trim()} className={btnPrimary}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
